/**
 * Offline queue for harvest days. Field workers often save with no signal; the day is
 * kept on the device and sent when the connection returns. Only whole-day saves are
 * queued (the RPC replaces the day, so replaying is idempotent).
 */
import { supabase } from './supabase';

const KEY = 'farmos.offline.harvest';

export interface QueuedHarvestDay {
  p_date: string;
  p_entries: { crop: string; greenhouse_id: string; kg: number | null; ng: number }[];
  p_note: string;
  p_note_he?: string;
  p_flags: string[];
  queuedAt: string;
}

function read(): QueuedHarvestDay[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '[]') as QueuedHarvestDay[];
  } catch {
    return [];
  }
}

function write(q: QueuedHarvestDay[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(q));
  } catch {
    /* storage full or unavailable: nothing more we can do offline */
  }
}

export const pendingHarvestDays = () => read();

export function enqueueHarvestDay(day: Omit<QueuedHarvestDay, 'queuedAt'>) {
  // A newer save of the same date replaces the queued one.
  write([
    ...read().filter((d) => d.p_date !== day.p_date),
    { ...day, queuedAt: new Date().toISOString() },
  ]);
}

export function isNetworkError(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  const msg = e instanceof Error ? e.message : ((e as { message?: string })?.message ?? String(e));
  return /Failed to fetch|NetworkError|Load failed|network/i.test(msg);
}

/** Send queued days; keeps the ones that still fail. Returns how many were sent. */
export async function flushHarvestQueue(): Promise<number> {
  const q = read();
  if (!q.length) return 0;
  const left: QueuedHarvestDay[] = [];
  let sent = 0;
  for (const { queuedAt: _q, ...day } of q) {
    const { error } = await supabase.rpc('save_harvest_day', day);
    if (!error) sent++;
    // Retry only transport failures; a rejection by the database (e.g. outside the
    // edit window) will never succeed, so it is dropped and logged.
    else if (isNetworkError(error)) left.push({ ...day, queuedAt: _q });
    else console.error('offline harvest day rejected', day.p_date, error);
  }
  write(left);
  return sent;
}
